import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  CssBaseline,
  TextField,
  Box,
  Typography,
  Container,
  FormControl,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import API from "../Utils/Interceptor/ApiMiddleware";
import CircularProgress from "@mui/material/CircularProgress";
import CustomSnackbar from "../components/Composables/CustomSnackBarMenu";
const LoginPage = () => {
  const defaultTheme = createTheme();
  const nav = useNavigate();

  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;

  const [state, setState] = useState({
    email: "",
    password: "",
    emailError: "",
    passwordError: "",
    isOtpStep: false,
    snackbar: {
      open: false,
      message: "",
      severity: "error",
    },
    showPassword: false,
    LoadingSpinner: false,
  });

  const [otp, setOtp] = useState(Array(6).fill(""));
  const otpRefs = useRef([]);

  const updateState = (field, value) => {
    setState((prev) => ({ ...prev, [field]: value }));
  };

  const updateSnackbar = (message, severity = "error") => {
    setState((prev) => ({
      ...prev,
      snackbar: { open: true, message, severity },
    }));
  };

  const handleSnackbarClose = () => {
    setState((prev) => ({
      ...prev,
      snackbar: { ...prev.snackbar, open: false },
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    let isValid = true;
    setState((prev) => ({ ...prev, LoadingSpinner: true }));
    if (!emailRegex.test(state.email)) {
      updateState("emailError", "Invalid email format");
      isValid = false;
      updateSnackbar("Invalid email format", "error");
      setState((prev) => ({ ...prev, LoadingSpinner: false }));
    } else {
      updateState("emailError", "");
    }

    if (!state.password) {
      updateState("passwordError", "Password Required");
      isValid = false;
      setState((prev) => ({ ...prev, LoadingSpinner: false }));
    } else {
      updateState("passwordError", "");
    }

    if (isValid) {
      API.post("/auth/login", { email: state.email, password: state.password })
        .then(() => {
          updateState("isOtpStep", true);
          updateSnackbar("OTP sent successfully", "success");
          setState((prev) => ({ ...prev, LoadingSpinner: false }));
        })
        .catch(() => {
          updateState("passwordError", "Invalid Credentials");
          updateSnackbar("Invalid Credentials", "error");
          setState((prev) => ({ ...prev, LoadingSpinner: false }));
        });
    }
  };

  const handleOtpVerify = async () => {
    if (otp.join("").length !== 6) {
      updateSnackbar("Please enter a valid 6-digit OTP", "error");
      return;
    }

    try {
      const response = await API.post("/auth/verify-otp", {
        email: state.email,
        otp: otp.join(""),
      });

      if (response?.data?.success) {
        updateSnackbar("Login Successful", "success");
        const token = response?.data?.data;
        if (token) {
          localStorage.setItem("email", token.email);
          localStorage.setItem("firstName", token.first_name);
          localStorage.setItem("empId", token.site_admin_id);
          nav("/dashboard");
        } else {
          nav("/login");
        }
      } else {
        updateSnackbar(response.data.message, "error");
      }
    } catch (error) {
      updateSnackbar("Invalid OTP", "error");
    }
  };

  const handleOtpChange = (e, index) => {
    const { value } = e.target;
    if (/^[0-9]?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < 5) {
        otpRefs.current[index + 1].focus();
      }
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      const newOtp = [...otp];
      newOtp[index] = "";
      setOtp(newOtp);

      if (index > 0) {
        otpRefs.current[index - 1].focus();
      }
    } else if (e.key === "Enter" && index === 5) {
      handleOtpVerify();
    }
  };

  const handleClickShowPassword = () => {
    updateState("showPassword", !state.showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  useEffect(() => {
    if (state.isOtpStep) {
      otpRefs.current[0]?.focus();
    }
  }, [state.isOtpStep]);

  return (
    <ThemeProvider theme={defaultTheme}>
      <Container component="main" maxWidth="sm">
        <CssBaseline />
        <Box
          sx={{
            marginTop: 15,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <img
            src="https://jobeze.com/logo/SiteAdminLogo.svg"
            style={{ width: "200px" }}
            alt="Site Admin Portal"
          />
        </Box>
        <Box
          sx={{
            marginTop: 5,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "20px",
            borderRadius: "16px",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            boxShadow: "0px 2px 4px 0px rgba(0, 0, 0, 0.20);",
          }}
        >
          <Typography component="h1" sx={{ my: 2 }} variant="h4">
            {state.isOtpStep ? "Verify OTP" : "Login"}
          </Typography>
          {state.isOtpStep ? (
            <>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                gap={5}
                sx={{ mb: 3 }}
              >
                {[...Array(6)].map((_, index) => (
                  <TextField
                    key={index}
                    id={`otp-input-${index}`}
                    inputRef={(ref) => (otpRefs.current[index] = ref)}
                    inputProps={{
                      maxLength: 1,
                      style: { textAlign: "center" },
                    }}
                    value={otp[index] || ""}
                    onChange={(e) => handleOtpChange(e, index)}
                    onKeyDown={(e) => handleOtpKeyDown(e, index)}
                    variant="outlined"
                    size="small"
                    sx={{ width: "40px" }}
                  />
                ))}
              </Box>
              <Button
                onClick={handleOtpVerify}
                fullWidth
                size="large"
                variant="contained"
                sx={{ py: 1 }}
              >
                Submit
              </Button>
            </>
          ) : (
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <TextField
                fullWidth
                label="Email Address"
                name="email"
                size="small"
                value={state.email}
                autoFocus
                autoComplete="email"
                onChange={(e) => updateState("email", e.target.value)}
                error={!!state.emailError}
                helperText={state.emailError}
              />
              <FormControl fullWidth sx={{ mt: 2 }} variant="outlined">
                <TextField
                  type={state.showPassword ? "text" : "password"}
                  label="Password"
                  size="small"
                  value={state.password}
                  onChange={(e) => updateState("password", e.target.value)}
                  error={!!state.passwordError}
                  helperText={state.passwordError}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                        >
                          {state.showPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>
              <Button
                type="submit"
                fullWidth
                size="large"
                variant="contained"
                sx={{ mt: 3, mb: 2, py: 1 }}
                disabled={state.LoadingSpinner}
              >
                {state.LoadingSpinner ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : (
                  "Log In"
                )}
              </Button>
            </Box>
          )}
        </Box>
        <CustomSnackbar
          open={state.snackbar.open}
          message={state.snackbar.message}
          severity={state.snackbar.severity}
          onClose={handleSnackbarClose}
        />
      </Container>
    </ThemeProvider>
  );
};

export default LoginPage;
